import type {
  BoundaryItem,
  DependencyRow,
  GlobalFilters,
  Product360Tab,
  Product360View,
  QueryResult,
  ReleaseFlowStage,
} from '@aurora-pvi/contracts';
import { CLASSIFICATION_CLASS_LABELS, ROUTES, buildPath } from '@aurora-pvi/contracts';
import { money, ratio, round } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, canEdit, resolved } from '../context';
import { applyFilters, rollUpBenefit, rollUpCost } from '../filters';
import { buildMetadata, currencyMetric, metricValue, moneyOrNull } from '../metadata';
import {
  applicationRef,
  departmentRef,
  ownerRef,
  productGroupRef,
  provenanceBadge,
  trendFrom,
  valueStreamRef,
} from '../refs';
import type { PagedRows, ProductListRow } from '../types';

/**
 * Products: the filterable list and the Product 360 (ref: 04_Product_360.png).
 *
 * The cost breakdown is built by taking the allocated total and splitting it by
 * the categories that produced it, so the four slices sum to the headline
 * figure exactly. `unallocated` travels alongside rather than being folded in.
 */

const CATEGORY_LABELS: Record<'Run' | 'Enhance' | 'Transform' | 'Other', string> = {
  Run: 'Run',
  Enhance: 'Enhance',
  Transform: 'Transform',
  Other: 'Other',
};

const BENEFIT_LABELS: Record<string, string> = {
  OperationalEfficiency: 'Operational Efficiency',
  CostAvoidance: 'Cost Avoidance',
  RevenueImpact: 'Revenue Impact',
  RiskMitigation: 'Risk Mitigation',
};

const ALL_TABS: Product360Tab[] = [
  'overview',
  'outcomes',
  'processes',
  'applications',
  'teams',
  'delivery',
  'financials',
  'suppliers',
  'contracts',
  'risks',
  'ai',
];

export function createProductRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  return {
    async list(filters: GlobalFilters): Promise<QueryResult<PagedRows<ProductListRow>>> {
      const scope = applyFilters(dataset, index, user, filters);

      const rows: ProductListRow[] = scope.products.map((product) => {
        const codes = new Set([product.businessCode]);
        const cost = rollUpCost(dataset, index, codes, { includeOwnerlessResidual: false });
        const benefit = rollUpBenefit(dataset, codes);
        const applications = index.applicationCodesByProduct.get(product.businessCode) ?? new Set();
        const processes = dataset.productProcessLinks.filter(
          (l) => l.productCode === product.businessCode,
        );

        return {
          id: product.id,
          businessCode: product.businessCode,
          name: product.name,
          classification: product.classification,
          classificationLabel: CLASSIFICATION_CLASS_LABELS[product.classification],
          valueStream: valueStreamRef(dataset, product.valueStreamCode),
          productGroup:
            product.parentKind === 'ProductGroup'
              ? productGroupRef(dataset, product.parentCode)
              : null,
          department: departmentRef(dataset, product.departmentCode),
          owner: ownerRef(index, user, product.ownerPersonCode),
          lifecycle: product.lifecycle,
          health: product.health,
          healthScore: product.healthScore > 0 ? product.healthScore : null,
          trend: trendFrom(product.healthHistory.slice(-6)),
          strategicTierLabel: product.strategicTierLabel,
          applicationCount: applications.size,
          processCount: processes.length,
          investmentYtd: moneyOrNull(user, cost.totalCents),
          benefitRealizedYtd: moneyOrNull(user, benefit.realizedCents),
          businessValue: moneyOrNull(user, product.businessValueCents),
          provenance: provenanceBadge(product.provenance),
          route: buildPath(ROUTES.product360, { productId: product.id }),
        };
      });

      return resolved({
        data: { rows, totalRows: rows.length, hasMore: false },
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.products,
          sources: PAGE_SOURCES.products,
          confidence: 'High',
          actions: [
            { action: 'export', permission: 'export', enabled: true },
            { action: 'createProduct', permission: 'create', enabled: canEdit(user) },
          ],
        }),
      });
    },

    async get360(productId: string, filters: GlobalFilters): Promise<QueryResult<Product360View>> {
      const scope = applyFilters(dataset, index, user, filters);
      const product =
        dataset.products.find((p) => p.id === productId) ??
        dataset.products.find((p) => p.businessCode === productId);

      if (!product || !scope.scopeProductCodes.has(product.businessCode)) {
        // Outside the caller's span. The response says nothing about whether the
        // product exists — an unauthorised reply that leaks existence is worse
        // than no reply at all.
        throw new Error('Product not found in scope');
      }

      const codes = new Set([product.businessCode]);
      const cost = rollUpCost(dataset, index, codes, { includeOwnerlessResidual: false });
      const benefit = rollUpBenefit(dataset, codes);
      const funding = dataset.fundingLines.find((f) => f.productCode === product.businessCode);
      const flow = dataset.flowMetrics.find((f) => f.productCode === product.businessCode);
      const incidents = dataset.incidents.filter((i) => i.productCode === product.businessCode);
      const workItems = dataset.workItems.filter((w) => w.productCode === product.businessCode);
      const releases = dataset.releases.filter((r) => r.productCode === product.businessCode);
      const applicationCodes =
        index.applicationCodesByProduct.get(product.businessCode) ?? new Set();

      const boundary = (inScope: boolean): BoundaryItem[] =>
        product.boundary
          .filter((entry) => entry.inScope === inScope)
          .map((entry) => ({
            id: entry.id,
            label: entry.label,
            entityRef: null,
            inScope: entry.inScope,
            reason: entry.reason,
            evidenceId: entry.evidenceCode
              ? (dataset.evidence.find((e) => e.businessCode === entry.evidenceCode)?.id ?? null)
              : null,
          }));

      const dependencies: DependencyRow[] = dataset.productDependencyLinks
        .filter((link) => link.productCode === product.businessCode)
        .map((link): DependencyRow | null => {
          const application = index.applicationsByCode.get(link.dependsOnApplicationCode);
          if (!application) return null;
          const supplier = application.vendorCode
            ? dataset.suppliers.find((s) => s.businessCode === application.vendorCode)
            : undefined;
          return {
            platform: applicationRef(application),
            provider: supplier?.name ?? 'Aurora internal',
            // The fixture's links are the product's own applications; it holds
            // no application-to-application layer, so nothing here is a
            // platform and nothing relies on anything else.
            kind: 'Application' as const,
            reliedOnBy: [],
            criticality: link.criticality,
            status:
              application.technicalHealth === 'Good'
                ? ('Healthy' as const)
                : application.technicalHealth === 'Degraded'
                  ? ('AtRisk' as const)
                  : application.technicalHealth === 'AtRisk'
                    ? ('HighRisk' as const)
                    : ('Critical' as const),
          };
        })
        .filter((row): row is DependencyRow => row !== null);

      const releaseFlow: ReleaseFlowStage[] = (
        ['Plan', 'Build', 'Test', 'Deploy', 'Done'] as const
      ).map((stage) => ({
        stage,
        // The status each stage counts, so a drilldown asks for the same rows
        // the number was built from.
        statusCode: (
          {
            Plan: 'Planned',
            Build: 'InFlight',
            Test: 'InTest',
            Deploy: 'Deploying',
            Done: 'Released',
          } as const
        )[stage],
        count: workItems.filter((w) => w.stage === stage).length,
        label:
          stage === 'Plan'
            ? 'Features'
            : stage === 'Build'
              ? 'In Progress'
              : stage === 'Test'
                ? 'In Test'
                : stage === 'Deploy'
                  ? 'Planned'
                  : 'Completed',
      }));

      // ── Cost breakdown that sums to the total ───────────────────────────────
      const categories = ['Run', 'Enhance', 'Transform', 'Other'] as const;
      const allocatedTotal = cost.allocatedCents;
      const costSummary = user.canSeeRestrictedFinance
        ? {
            total: {
              allocated: money(cost.allocatedCents),
              unallocated: money(cost.unallocatedCents),
              total: money(cost.totalCents),
              allocationCoverage: cost.coverage,
              basis: 'Direct allocation with usage and headcount apportionment',
            },
            percentOfPlan: funding
              ? round(cost.totalCents / Math.max(1, funding.plannedCents * (140 / 365)), 4)
              : null,
            breakdown: categories
              .map((category) => ({
                category: CATEGORY_LABELS[category],
                amount: money(cost.byCategory[category]),
                percent: ratio(cost.byCategory[category], Math.max(1, allocatedTotal)),
              }))
              .filter((slice) => slice.amount.amount !== '0.00'),
          }
        : null;

      const benefitByCategory = new Map<string, number>();
      for (const record of dataset.benefits.filter((b) => b.productCode === product.businessCode)) {
        benefitByCategory.set(
          record.category,
          (benefitByCategory.get(record.category) ?? 0) + record.realizedCents,
        );
      }
      const benefitSummary = user.canSeeRestrictedFinance
        ? {
            realized: money(benefit.realizedCents),
            percentOfTarget:
              benefit.targetCents === 0
                ? null
                : round(benefit.realizedCents / benefit.targetCents, 4),
            breakdown: Array.from(benefitByCategory.entries()).map(([category, amount]) => ({
              category: BENEFIT_LABELS[category] ?? category,
              amount: money(amount),
              percent: ratio(amount, Math.max(1, benefit.realizedCents)),
            })),
          }
        : null;

      const financialDrivers = user.canSeeRestrictedFinance
        ? dataset.benefits
            .filter((b) => b.productCode === product.businessCode)
            .sort((a, b) => b.realizedCents - a.realizedCents)
            .slice(0, 5)
            .map((record) => ({
              driver: record.driver,
              type: BENEFIT_LABELS[record.category] ?? record.category,
              realized: money(record.realizedCents),
              vsTargetPercent:
                record.targetCents === 0
                  ? null
                  : round(record.realizedCents / record.targetCents - 1, 4),
            }))
        : [];

      const roi = cost.totalCents === 0 ? null : round(benefit.realizedCents / cost.totalCents, 2);

      const view: Product360View = {
        product: {
          id: product.id,
          businessCode: product.businessCode,
          name: product.name,
          classification: product.classification,
          domain: departmentRef(dataset, product.departmentCode),
          productGroup:
            product.parentKind === 'ProductGroup'
              ? productGroupRef(dataset, product.parentCode)
              : null,
          valueStream: valueStreamRef(dataset, product.valueStreamCode),
          owner: ownerRef(index, user, product.ownerPersonCode),
          lifecycle: product.lifecycle,
          health: product.health,
          healthScore: product.healthScore > 0 ? product.healthScore : null,
          strategicTier: product.strategicTier,
          strategicTierLabel: product.strategicTierLabel,
        },
        headerKpis: [
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-020',
            label: 'Business Value',
            cents: product.businessValueCents,
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Up',
          }),
          metricValue({
            dataset,
            metricId: 'MET-021',
            label: 'Health Score',
            value: product.healthScore,
            unit: 'score',
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Up',
            confidence: 'High',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-022',
            label: 'Investment (YTD)',
            cents: cost.totalCents,
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Context',
          }),
          metricValue({
            dataset,
            metricId: 'MET-023',
            label: 'ROI (TTM)',
            value: user.canSeeRestrictedFinance ? roi : null,
            unit: 'ratio',
            status: user.canSeeRestrictedFinance ? 'Available' : 'Unavailable',
            sensitivity: 'Restricted',
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Up',
            unavailableReason: 'Financial values are restricted for your role.',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-024',
            label: 'Benefit Realised (YTD)',
            cents: benefit.realizedCents,
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Up',
          }),
        ],
        mission: { statement: product.mission, briefUrl: `/products/${product.id}/brief` },
        customers: product.customers,
        // The fixture holds no process catalogue — it lives in core.process
        // now (0054). An empty list renders the tab's own honest empty state
        // rather than a fabricated map.
        processes: [],
        okrs: product.okrs.map((okr) => ({
          id: okr.id,
          ordinal: okr.ordinal,
          objective: okr.objective,
          targetLabel: okr.targetLabel,
          actualLabel: okr.actualLabel,
          progressPercent: okr.progress,
          onTrack: okr.onTrack,
          // The generated fixture measures every key result it invents, so it
          // never produces the unmeasurable case. Null rather than a sentence,
          // because a fixture claiming a reason would be a fabricated one.
          unmeasurableReason: null,
        })),
        boundary: { inScope: boundary(true), outOfScope: boundary(false) },
        healthDimensions: product.healthDimensions,
        keyKpis: [
          metricValue({
            dataset,
            metricId: 'MET-030',
            label: 'Deployment Frequency',
            value: flow?.deploymentFrequencyPerMonth ?? null,
            unit: 'deploymentsPerPeriod',
            status: flow ? 'Available' : 'Unavailable',
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Up',
            unavailableReason: 'No delivery telemetry for this product in the selected period.',
          }),
          metricValue({
            dataset,
            metricId: 'MET-031',
            label: 'Lead Time for Change',
            value: flow?.leadTimeDays ?? null,
            unit: 'days',
            status: flow ? 'Available' : 'Unavailable',
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Down',
            unavailableReason: 'No delivery telemetry for this product in the selected period.',
          }),
          metricValue({
            dataset,
            metricId: 'MET-032',
            label: 'Change Failure Rate',
            value: flow?.changeFailureRate ?? null,
            unit: 'percent',
            status: flow ? 'Available' : 'Unavailable',
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Down',
            unavailableReason: 'No delivery telemetry for this product in the selected period.',
          }),
          metricValue({
            dataset,
            metricId: 'MET-033',
            label: 'P1 Incidents',
            value: incidents.filter((i) => i.severity === 'P1').length,
            unit: 'count',
            subjectId: product.id,
            subjectType: 'Product',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
        ],
        dependencies,
        releaseFlow,
        suppliers: [],
        upcomingReleases: releases
          .filter((release) => release.status === 'Planned' || release.status === 'InFlight')
          .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate))
          .slice(0, 3)
          .map((release) => ({
            code: release.businessCode,
            name: release.name,
            plannedDate: release.plannedDate,
          })),
        costSummary,
        benefitSummary,
        financialDrivers,
        aiImpact: {
          // Use-case count and automation rate are operational facts, so they
          // stay visible; the two figures denominated in money and effort are
          // masked to null for a caller without finance entitlement.
          useCasesInProduction: product.aiUseCasesInProduction,
          automationRate: product.aiAutomationRate,
          costSavings: moneyOrNull(user, product.aiCostSavingsCents),
          hoursSaved: user.canSeeRestrictedFinance ? product.aiHoursSaved : null,
        },
        stats: {
          goLiveDate: product.goLiveDate,
          totalReleases: product.totalReleases,
          featuresDelivered: product.featuresDelivered,
          activeUsers: product.activeUsers,
          dailyTransactions: product.dailyTransactions,
          integrations: applicationCodes.size,
          countries: product.countries,
        },
        availableTabs: ALL_TABS.filter((tab) => {
          if ((tab === 'financials' || tab === 'contracts') && !user.canSeeRestrictedFinance) {
            return false;
          }
          if (tab === 'teams' && !user.canSeeWorkforce) return false;
          return true;
        }),
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: buildPath(ROUTES.product360, { productId: product.id }),
          sources: PAGE_SOURCES.product360,
          confidence: product.provenance.confidenceBand,
          actions: [
            { action: 'export', permission: 'export', enabled: true },
            {
              action: 'editProduct',
              permission: 'edit',
              enabled: canEdit(user) && scope.productCodes.has(product.businessCode),
            },
            {
              action: 'proposeRelationship',
              permission: 'propose',
              enabled: canEdit(user),
            },
          ],
        }),
      });
    },
  };
}
